import { gql } from '@apollo/client';

export const MultimediaFragment = gql`
  fragment MultimediaFragment on Multimedia {
    src
    alt
    position
    variant
    active
    _id
    updatedAt
    description
  }
`;

export const OptionFragment = gql`
  fragment OptionFragment on Option {
    name
    values
    active
    _id
  }
`;

export const PaginationFragment = gql`
  fragment PaginationFragment on PageInfo {
    currentPage
    perPage
    pageCount
    itemCount
    hasNextPage
    hasPreviousPage
  }
`;

export const CategoryFragment = gql`
  fragment CategoryFragment on Category {
    name
    active
    _id
  }
`;
export const CommentFragment = gql`
  fragment CommentFragment on Comment {
    title
    text
    rating
    client
    active
    _id
    createdAt
    updatedAt
  }
`;
export const OrderTableFragment = gql`
  fragment OrderTableFragment on Order {
    _id
    code
    status
    paymentStatus
  }
`;

export const BenefitFragment = gql`
  fragment BenefitFragment on Benefit {
    _id
    title
    price
    value
    active
  }
`;
export const SubscriptionFragment = gql`
  fragment SubscriptionFragment on Subscription {
    _id
    title
    plan {
      _id
    }
    frequency {
      _id
    }
    client
    price
    discountCoupons
    subscription
    shopCart
    order
    active
  }
`;

export const AddressFragment = gql`
  fragment AddressFragment on Address {
    country
    state
    city
    municipality
    zip
    address1
    address2
    location {
      type
      coordinates
    }
    extra
    active
    _id
    createdAt
    updatedAt
  }
`;

export const PromoCodeFragment = gql`
  fragment PromoCodeFragment on PromoCode {
    _id
    active
    clients
    code
    createdAt
    expirationDate
    name
    products
    slug
    type
    updatedAt
    value
  }
`;

export const TaxFragment = gql`
  fragment TaxFragment on Tax {
    name
    value
    active
    _id
  }
`;

export const CurrencyFragment = gql`
  fragment CurrencyFragment on Currency {
    name
    symbol
    rate
    active
    _id
  }
`;

export const UserFragment = gql`
  fragment UserFragment on User {
    _id
    firstName
    lastName
    userType
    privilege
    email
    dni
    dniType
    phone
    firstLogin
    company {
      _id
      name
    }
    client {
      _id
      wishlist {
        _id
      }
      shopCart {
        _id
        code
        active
        user
        subscriptions {
          _id
        }
        products {
          _id
          active
          client
          company
          createdAt
          discount
          title
          option {
            _id
            option1
            option2
            option3
          }
          order
          photos {
            ...MultimediaFragment
          }
          product {
            _id
            slug
            title
            description
            company
          }
          price
          taxes {
            _id
            active
            name
            value
          }
          variant {
            _id
            title
            sku
            price
            photos {
              ...MultimediaFragment
            }
          }
          shopCart
          sku
        }
        benefits {
          _id
        }
      }
    }
    active
    photo {
      ...MultimediaFragment
    }
  }
  ${MultimediaFragment}
`;

export const CompanyFragment = gql`
  fragment CompanyFragment on Company {
    name
    logo {
      ...MultimediaFragment
    }
    email
    dniType
    phone
    _id
  }
  ${MultimediaFragment}
`;

export const VariantFragment = gql`
  fragment VariantFragment on Variant {
    title
    sku
    price
    compareAtPrice
    active
    _id
    stock
    option {
      option1
      option2
      option3
      _id
    }
    photos {
      ...MultimediaFragment
    }
  }
  ${MultimediaFragment}
`;

export const ProductFragment = gql`
  fragment ProductFragment on Product {
    _id
    slug
    title
    priority
    rating
    points
    reviews
    company
    description
    variants {
      ...VariantFragment
    }
    status
    active
    comments {
      ...CommentFragment
    }
    category {
      ...CategoryFragment
    }
  }
  ${VariantFragment}
  ${CategoryFragment}
  ${CommentFragment}
`;

export const OrderProductFragment = gql`
  fragment OrderProductFragment on OrderProduct {
    _id
    active
    client
    company
    createdAt
    discount
    title
    option {
      _id
      option1
      option2
      option3
    }
    order
    photos {
      ...MultimediaFragment
    }
    product {
      _id
      slug
      title
      description
      company
    }
    price
    taxes {
      _id
      active
      name
      value
    }
    variant {
      _id
      title
      sku
      price
      photos {
        ...MultimediaFragment
      }
    }
    shopCart
    sku
  }
  ${MultimediaFragment}
`;

export const OrderFragment = gql`
  fragment OrderFragment on Order {
    active
    _id
    benefitsApplied {
      _id
    }
    billingAddress {
      ...AddressFragment
    }
    code
    companies
    discountTotal
    extraFee
    createdAt
    paymentInfo {
      _id
      active
      amount
      createdAt
      metadata
      paymentMethod {
        title
        _id
      }
      reference
    }
    paymentStatus
    pickupAddress {
      ...AddressFragment
    }
    products {
      ...OrderProductFragment
    }
    promoCodes {
      _id
      code
    }
    shippingAddress {
      ...AddressFragment
    }
    status
    subTotal
    taxBase
    taxTotal
    total
    user {
      ...UserFragment
    }
  }
  ${AddressFragment}
  ${OrderProductFragment}
  ${UserFragment}
`;

export const OrderBenefitFragment = gql`
  fragment OrderBenefitFragment on OrderBenefit {
    _id
    active
    benefit {
      ...BenefitFragment
    }
    client
    createdAt
    order
    shopCart
    title
    updatedAt
    value
  }
  ${BenefitFragment}
`;

export const ShopCartFragment = gql`
  fragment ShopCartFragment on ShopCart {
    _id
    code
    active
    user
    subscriptions {
      _id
    }
    products {
      ...OrderProductFragment
    }
    benefits {
      _id
    }
  }
  ${OrderProductFragment}
`;

export const ClientFragment = gql`
  fragment ClientFragment on Client {
    user
    wishlist {
      ...ProductFragment
    }
  }
  ${ProductFragment}
`;

export const CatalogFragment = gql`
  fragment CatalogFragment on CatalogInfo {
    sections {
      category {
        ...CategoryFragment
      }
      count
      products {
        ...ProductFragment
      }
    }
    benefits {
      ...BenefitFragment
    }
  }
  ${CategoryFragment}
  ${ProductFragment}
  ${BenefitFragment}
`;

export const PaymentDataFragment = gql`
  fragment PaymentDataFragment on PaymentData {
    keyId
    publicKeyId
    bank
    phone
    dniType
    dni
    email
    _id
  }
`;

export const PaymentMethodFragment = gql`
  fragment PaymentMethodFragment on PaymentMethod {
    _id
    title
    commission
    automated
    active
    note
    slug
    currency {
      ...CurrencyFragment
    }
    taxes {
      ...TaxFragment
    }
    extraFields {
      name
      value
    }
    paymentData {
      ...PaymentDataFragment
    }
  }
  ${CurrencyFragment}
  ${TaxFragment}
  ${PaymentDataFragment}
`;

export const PlanFragment = gql`
  fragment PlanFragment on Plan {
    title
    updatedAt
    totalCoupons
    frequencies {
      time
      price
      _id
      createdAt
      updatedAt
    }
    createdAt
    _id
    active
    discountCoupons
    discountCouponPrice
    couponPrice
    freeCoupons
  }
`;

export const SettingsFragment = gql`
  fragment SettingsFragment on Settings {
    pickupAddresses
    carouselImages {
      title
      description
      url
      linkType
      link
      _id
    }
    termsAndConditions
    whatsAppUrl
    frequentlyAskedQuestions
    supportUrl
    _id
  }
`;

export const ColorsFragment = gql`
  fragment ColorsFragment on Color {
    _id
    name
    shades
  }
`;

export const AboutFragment = gql`
  fragment AboutFragment on About {
    _id
    description
    image
    title
  }
`;

export const FooterFragment = gql`
  fragment FooterFragment on Footer {
    _id
    address
    email
    phone
    socialLinks {
      _id
      platform
      url
    }
    text
  }
`;

export const BannerFragment = gql`
  fragment BannerFragment on Banner {
    _id
    buttons {
      _id
      action
      link
      title
    }
    image
    link
    seccion
    text
    title
  }
`;

export const HomeCardsFragment = gql`
  fragment HomeCardsFragment on HomeCard {
    _id
    description
    image
    link
    title
  }
`;
